import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { addToCart, removeFromCart, updateCartItemQuantity, upsertCartItem, clearCart } from "../../../redux/slices/cartSlice";
import { setPaymentQR, setTempDataId } from "../../../redux/slices/qrScanSlice";
import { ArrowRight, AlertTriangle, Package, LogIn, User } from "react-feather";
import Cart from "../../../compoment/icon/Cart";
import Heart from "../../../compoment/icon/Heart";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import PrintSizeModal from "../../../modal/PrintSizeModal";
import CartModal from "../../../modal/CartModal";
import GuestEmailModal from "../../../modal/GuestEmailModal";
import MerchantiseItemModal from "../../../modal/MerchantiseItemModal";
import PackItemModal from "../../../modal/PackItemModal";
import MerchandiseSelectionList from "../../../compoment/features/MerchandiseSelectionList";
import AddtoCart from "../../../modal/AddtoCart";
import LogoutSuccessModal from "../../../modal/LogoutSuccessModal";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";
import LoaderSaveModal from "../../../modal/LoaderSaveModal";
import {
    fetchAllProducts,
    findPreferredProductColor,
    findPreferredProductSize,
    formatProductSizeWithInches,
    getAvailableStock,
    getProductColorLabel,
    isProductInStock,
    Product,
    uniqueSortedProductSizes,
} from "../../../services/productService";
import {
    applyMerchGenerateItemsToProducts,
    generateMerchMockups,
    getCachedMerchGenerateResponse,
    readPrintFrameMerchPageCache,
    writePrintFrameMerchPageCache,
} from "../../../services/merchGenerateService";
import { enrichPacksWithCatalogProducts, getPackDisplayImage, getPackDisplayPrice, getPackUnitTotalFromSelections, mapPackProductsForCart, mapProductsToPacks, Pack, PackProductSelection } from "../../../services/packService";
import Loader from "../../../compoment/ui/Loader";
import { formatFrameSize } from "../../../utils";
import { isMerchCartType, isMultiStickerPackCartItem, merchCartLineMatches, normalizeCartArtKey, resolveCartItemArtUrl, resolveCartItemType, resolveOrderLineImageUrl, resolveSessionArtUrl, resolveSingleStickerMockupUrl } from "../../../utils/cartArtUtils";
import { calculateMuseumFeeTaxTotals } from "../../../utils/museumFeeTax";
import { loadMuseumFeeTax } from "../../../redux/slices/museumSlice";
import {
    enrichStationWithThemeBackgrounds,
    persistStationSelection,
    readActiveStoredStation,
    readStoredStationData,
    resolveStationCategorySlug,
    resolveStationRouteSlug,
} from "../../../utils/stationFlowStorage";
import {
    isCaricaturePortraitFlow,
    readPortraitSubject,
    readSelectedAiSelfieArtFromStorage,
} from "../../../services/stylePromptResolver";
import {
    persistFavouriteTransparencyFlag,
    readFavouriteTransparencyFlag,
} from "../../../services/caricatureHobbyService";
import SigninModal from "../../../modal/SigninModal";
import SignupModal from "../../../modal/SignupModal";
import ForgotPasswordModal from "../../../modal/ForgotPasswordModal";
import { AuthUser } from "../../../modal/auth/types";
import { authService } from "../../../modal/auth/authService";
import { getStoredAuthUser, persistAuthUser, clearStoredAuthUser, resolveAuthAccessToken } from "../../../utils/authSession";
import {
    getUserFavourites,
    FAVOURITES_PAGE_SIZE,
    checkFavouriteImageAlreadySaved,
    isFavouriteArtSavedLocally,
    isFavouriteImageInList,
    readFavouriteIsAlreadySaved,
} from "../../../services/favouritesService";
import { setFavouritesPage, appendFavourites, setFavouritesError, startLoadingFavourites, startLoadingMoreFavourites, clearFavourites } from "../../../redux/slices/favouritesSlice";
import { resolvePaymentOrderType, resolvePaymentRedirectUrl } from "../../../config/apiUrls";
import { createTempData, resolvePaymentUserId, scanQRPaymentAPI } from "../../../services/paymentService";
import { createPayOnReceptionOrder } from "../../../services/orderService";
import {
    persistPaymentSessionFromTempData,
    savePendingPaymentOrderFromApiOrders,
} from "../../../utils/pendingPaymentOrder";
import { useDelayedNavigation } from "../../../hooks/useDelayedNavigation";

import PackOne from "../../../assets/img/new-theme/pack-1.png";
import { Favourite } from "../../../services/favouritesService";
import FavouriteArtModal from "../../../modal/FavouriteArtModal";
import { saveArtAndHobbyFavouritesAfterLogin } from "../../../utils/hobbyGenerationNavigation";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
// Favourites are loaded elsewhere into Redux; this screen only reads them

function normalizeCartVariety(variety: unknown): string | undefined {
    if (!variety) return undefined;
    if (typeof variety === "string") return variety;
    if (typeof variety === "object" && variety !== null && "name" in variety) {
        return String((variety as { name?: unknown }).name || "");
    }
    return String(variety);
}

type DisplayCartItem = {
    id: string;
    name: string;
    dimensions: string;
    variety?: string;
    color?: string;
    size?: string;
    finalCanvasImage?: string;
    price: number;
    imgURL?: string;
    quantity: number;
    product_sizes?: any

};

function getMerchColorLabel(color: any): string {
    return getProductColorLabel(color);
}

function findMerchCartItemForProduct(
    product: Product,
    cartItems: any[],
    artSource?: string | null
) {
    const defaultColor =
        findPreferredProductColor(product.color, product) || product.color?.[0] || null;
    const defaultSize =
        findPreferredProductSize(product, defaultColor) ||
        uniqueSortedProductSizes(product.product_sizes)[0] ||
        null;
    const sizeLabel = defaultSize ? formatProductSizeWithInches(defaultSize) : undefined;
    const colorLabel = defaultColor ? getMerchColorLabel(defaultColor) : undefined;

    return cartItems.find((item) =>
        merchCartLineMatches(item, product.id, {
            size: sizeLabel,
            variety: colorLabel,
            color: colorLabel,
            finalCanvasImage: artSource,
            type: resolveCartItemType(product.type),
        })
    );
}

const PrintFrameSelection = () => {
    const delayedNavigate = useDelayedNavigation();
    const [selectedFrames, setSelectedFrames] = useState<{ [key: number]: number }>({});
    const [selectedFramesCount, setSelectedFramesCount] = useState<{ [key: number]: number }>({});
    const [selectedMerchandise, setSelectedMerchandise] = useState<{ [key: number]: number }>({});
    const [selectedMerchandiseCount, setSelectedMerchandiseCount] = useState<{ [key: number]: number }>({});
    const [showFavouriteModal, setShowFavouriteModal] = useState<boolean>(false);
    const [signinModal, setSigninModal] = useState<boolean>(false);
    const [signupModal, setSignupModal] = useState<boolean>(false);
    const [forgotPasswordModal, setForgotPasswordModal] = useState<boolean>(false);
    const [authUser, setAuthUser] = useState<AuthUser | null>(() => getStoredAuthUser());
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [showGuestEmailModal, setShowGuestEmailModal] = useState(false);

    const selectedFramesCountRef = useRef(selectedFramesCount);
    const selectedMerchandiseCountRef = useRef(selectedMerchandiseCount);

    useEffect(() => {
        selectedFramesCountRef.current = selectedFramesCount;
    }, [selectedFramesCount]);

    useEffect(() => {
        selectedMerchandiseCountRef.current = selectedMerchandiseCount;
    }, [selectedMerchandiseCount]);

    const [successModal, setSuccessModal] = useState(false);
    const [logoutSuccessModal, setLogoutSuccessModal] = useState(false);
    const minBackSaveLoaderDurationMs = 3000;
    const [showSignOutConfirmModal, setShowSignOutConfirmModal] = useState(false);
    /** After Skip → logout confirm → success, continue to thank-you. */
    const [pendingSkipAfterLogout, setPendingSkipAfterLogout] = useState(false);
    /** Back: save-art prompt → (Yes) login or (No) home confirm. */
    const [showSaveArtBackModal, setShowSaveArtBackModal] = useState(false);
    const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
    const [showSkipConfirmModal, setShowSkipConfirmModal] = useState(false);
    /** Skip under Sign In only for Back → save-art → Yes login flow. */
    const [showSigninSkipButton, setShowSigninSkipButton] = useState(false);
    const pendingHomeAfterSaveLoginRef = useRef(false);
    /** Set when logout-success should navigate home (not cleared by login modal onHide). */
    const pendingNavigateHomeAfterLogoutRef = useRef(false);
    /** After Pay Now / Pay at Counter login, open cart and continue checkout. */
    const pendingCheckoutAfterLoginRef = useRef(false);
    /** While login handler owns favourites sync, skip the sessionUser useEffect fetch. */
    const authFavouritesSyncRef = useRef(false);
    const lastFavouritesUserIdRef = useRef<string | null>(null);
    const [blockProductPointer, setBlockProductPointer] = useState(false);
    const suppressProductOpenUntilRef = useRef(0);
    const blockProductPointerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wasOverlayOpenRef = useRef(false);
    const merchGenerateDoneKeyRef = useRef<string>("");
    const catalogMerchandiseRef = useRef<Product[]>([]);
    const generatedMerchandiseRef = useRef<Product[]>([]);
    const [isSavingFavourites, setIsSavingFavourites] = useState(false);
    const [saveArtSuccess, setSaveArtSuccess] = useState(false);
    const minSaveLoaderDurationMs = 2500;
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [frameProducts, setFrameProducts] = useState<Product[]>([]);
    /** Rendered only after merch/generate mockup images are applied. */
    const [merchandiseProducts, setMerchandiseProducts] = useState<Product[]>([]);
    const [merchandiseProductsCart, setMerchandiseProductsCart] = useState<Product[]>([]);
    const [packs, setPacks] = useState<Pack[]>([]);
    const [selectedPacks, setSelectedPacks] = useState<{ [key: number]: number }>({});
    const [selectedPacksCount, setSelectedPacksCount] = useState<{ [key: number]: number }>({});

    const [printSizeModal, setPrintSizeModal] = useState(false);
    const [showCartModal, setShowCartModal] = useState(false);
    const [merchandiseModal, setMerchandiseModal] = useState({
        show: false,
        item: {} as Product,
        index: -1,
        initialQuantity: 0,
    });
    const [packModal, setPackModal] = useState({
        show: false,
        pack: null as Pack | null,
        index: -1,
        initialQuantity: 1,
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { slug: slugParam } = useParams<{ slug: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const routeState = location.state || {};
    const stationData =
        routeState.stationData || readActiveStoredStation() || readStoredStationData();
    const { snapshot, selectedArt } = routeState;
    const processedImageUrl = useSelector((s: RootState) => s.uploadFlow?.processedImageUrl);
    const cartItems = useSelector((s: RootState) => s.cart?.items || []);
    const {
        processingFeePercent,
        salesTaxPercent,
        loaded: museumLoaded,
        loading: museumLoading,
    } = useSelector((s: RootState) => s.museum);
    const { firstName, lastName, ticketId, userId, scanResult } = useSelector((s: RootState) => s.qrScan || {
        firstName: null,
        lastName: null,
        ticketId: null,
        userId: null,
        scanResult: null
    });
    const originalSessionArt = useMemo(
        () => resolveSessionArtUrl({ snapshot, processedImageUrl, cartItems }),
        [snapshot, processedImageUrl, cartItems]
    );
    const [selectedFavouriteUrl, setSelectedFavouriteUrl] = useState<string | null>(null);
    const currentArt = useMemo(
        () => selectedFavouriteUrl || originalSessionArt,
        [selectedFavouriteUrl, originalSessionArt]
    );

    const isCaricaturePortrait = useMemo(
        () =>
            isCaricaturePortraitFlow({
                slug: slugParam,
                stationType: stationData?.type,
                artName:
                    selectedArt?.name ||
                    readSelectedAiSelfieArtFromStorage()?.name,
            }),
        [slugParam, stationData?.type, selectedArt?.name]
    );

    /** TEMP test target: caricature-portrait → Pets (not People). */
    const isPortraitPetSubject =
        isCaricaturePortrait && readPortraitSubject() === "pet";

    useEffect(() => {
        if (isCaricaturePortrait) {
            persistFavouriteTransparencyFlag(true);
        }
    }, [isCaricaturePortrait]);

    const resolveCategoryAllRoute = useCallback((): string => {
        const categorySlug = resolveStationCategorySlug(stationData, slugParam);
        return categorySlug ? `/${categorySlug}/all` : "/";
    }, [slugParam, stationData]);

    const goToCategoryAll = useCallback(() => {
        const activeStation =
            stationData && Object.keys(stationData).length > 0
                ? stationData
                : readActiveStoredStation();
        if (activeStation?.type?.trim()) {
            persistStationSelection(activeStation);
        }
        navigate(resolveCategoryAllRoute(), {
            replace: true,
            state: {
                stationData: activeStation,
                selectedArt: selectedArt || readSelectedAiSelfieArtFromStorage(),
            },
        });
    }, [navigate, resolveCategoryAllRoute, selectedArt, stationData]);

    useEffect(() => {
        if (currentArt) return;

        const timer = window.setTimeout(() => {
            const resolvedArt = resolveSessionArtUrl({ snapshot, processedImageUrl, cartItems });
            if (resolvedArt || selectedFavouriteUrl) return;

            goToCategoryAll();
        }, 400);

        return () => window.clearTimeout(timer);
    }, [
        cartItems,
        currentArt,
        goToCategoryAll,
        processedImageUrl,
        selectedFavouriteUrl,
        snapshot,
    ]);

    const merchandiseProductsMemo = useMemo(() =>
        merchandiseProducts.length > 0 ? merchandiseProducts : undefined,
        [merchandiseProducts]
    );

    /** Same generate mockup Single Sticker sends on order — reused for N-sticker packs. */
    const singleStickerMockupUrl = useMemo(
        () => resolveSingleStickerMockupUrl(merchandiseProductsMemo),
        [merchandiseProductsMemo]
    );

    const frameProductsMemo = useMemo(() =>
        frameProducts.length > 0 ? frameProducts : undefined,
        [frameProducts]
    );

    const selectedMerchandiseRef = useRef(selectedMerchandise);
    useEffect(() => {
        selectedMerchandiseRef.current = selectedMerchandise;
    }, [selectedMerchandise]);

    useEffect(() => {
        if (!merchandiseProductsMemo || merchandiseProductsMemo.length === 0) return;

        const merchandiseCartItems = cartItems.filter(
            (item) =>
                isMerchCartType(item.type) &&
                !item.isPack &&
                normalizeCartArtKey(item.finalCanvasImage) === normalizeCartArtKey(currentArt)
        );
        const cartItemsKey = JSON.stringify(
            merchandiseCartItems.map((item) => ({
                id: String(item.productId || item.id),
                quantity: item.quantity,
                size: item.size,
                variety: item.variety,
                color: item.color,
                art: normalizeCartArtKey(item.finalCanvasImage),
            }))
        );

        if (prevMerchCartItemsRef.current === cartItemsKey) return;
        prevMerchCartItemsRef.current = cartItemsKey;

        const cartProductIds = new Set(merchandiseCartItems.map(item => String(item.productId || item.id)));
        const currentSelected = selectedMerchandiseRef.current;
        const currentCounts = selectedMerchandiseCountRef.current;
        const indicesToUnselect = new Set<number>();
        const indicesToUpdate = new Map<number, number>();

        Object.keys(currentSelected).forEach(indexStr => {
            const index = Number(indexStr);
            const product = merchandiseProductsMemo[index];

            if (product && !cartProductIds.has(String(product.id))) {
                indicesToUnselect.add(index);
            }
        });

        merchandiseCartItems.forEach(cartItem => {
            const productIndex = merchandiseProductsMemo.findIndex(
                p => String(p.id) === String(cartItem.productId || cartItem.id)
            );
            if (productIndex !== -1) {
                const currentCount = currentCounts[productIndex] ?? 0;
                if (cartItem.quantity !== currentCount) {
                    indicesToUpdate.set(productIndex, cartItem.quantity);
                }
            }
        });

        if (indicesToUpdate.size > 0) {
            setSelectedMerchandiseCount((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToUpdate.forEach((quantity, index) => {
                    if (updated[index] !== quantity) {
                        updated[index] = quantity;
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }

        if (indicesToUnselect.size > 0) {
            setSelectedMerchandise((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToUnselect.forEach(index => {
                    if (updated[index] !== undefined) {
                        delete updated[index];
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });

            setSelectedMerchandiseCount((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToUnselect.forEach(index => {
                    if (updated[index] !== undefined) {
                        delete updated[index];
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }

        const indicesToSelect = new Map<number, number>();
        merchandiseCartItems.forEach(cartItem => {
            const productIndex = merchandiseProductsMemo.findIndex(
                p => String(p.id) === String(cartItem.productId || cartItem.id)
            );
            if (productIndex !== -1 && !currentSelected[productIndex]) {
                indicesToSelect.set(productIndex, cartItem.quantity);
            }
        });

        if (indicesToSelect.size > 0) {
            setSelectedMerchandise((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToSelect.forEach((_quantity, index) => {
                    if (updated[index] === undefined) {
                        updated[index] = 1;
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });

            setSelectedMerchandiseCount((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToSelect.forEach((quantity, index) => {
                    if (updated[index] !== quantity) {
                        updated[index] = quantity;
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }
    }, [cartItems, merchandiseProductsMemo, currentArt]);

    const selectedFramesRef = useRef(selectedFrames);
    const selectedFramesCountRefForSync = useRef(selectedFramesCount);
    const prevCartItemsRef = useRef<string>('');
    const prevMerchCartItemsRef = useRef<string>('');

    useEffect(() => {
        selectedFramesRef.current = selectedFrames;
    }, [selectedFrames]);

    useEffect(() => {
        selectedFramesCountRefForSync.current = selectedFramesCount;
    }, [selectedFramesCount]);

    useEffect(() => {
        if (!frameProductsMemo || frameProductsMemo.length === 0) return;

        const frameCartItems = cartItems.filter(item => item.type === 'onsite-printing');
        const cartItemsKey = JSON.stringify(frameCartItems.map(item => ({ id: String(item.id), quantity: item.quantity })));

        if (prevCartItemsRef.current === cartItemsKey) return;
        prevCartItemsRef.current = cartItemsKey;

        const cartProductIds = new Set(frameCartItems.map(item => String(item.id)));
        const currentSelected = selectedFramesRef.current;
        const currentCounts = selectedFramesCountRefForSync.current;
        const indicesToUnselect = new Set<number>();
        const indicesToUpdate = new Map<number, number>();

        Object.keys(currentSelected).forEach(indexStr => {
            const index = Number(indexStr);
            const product = frameProductsMemo[index];

            if (product && !cartProductIds.has(String(product.id))) {
                indicesToUnselect.add(index);
            }
        });

        frameCartItems.forEach(cartItem => {
            const productIndex = frameProductsMemo.findIndex(p => String(p.id) === String(cartItem.id));
            if (productIndex !== -1) {
                const currentCount = currentCounts[productIndex] || 1;
                if (cartItem.quantity !== currentCount) {
                    indicesToUpdate.set(productIndex, cartItem.quantity);
                }
            }
        });

        if (indicesToUpdate.size > 0) {
            setSelectedFramesCount((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToUpdate.forEach((quantity, index) => {
                    if (updated[index] !== quantity) {
                        updated[index] = quantity;
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }

        if (indicesToUnselect.size > 0) {
            setSelectedFrames((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToUnselect.forEach(index => {
                    if (updated[index] !== undefined) {
                        delete updated[index];
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });

            setSelectedFramesCount((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToUnselect.forEach(index => {
                    if (updated[index] !== undefined) {
                        delete updated[index];
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }

        const indicesToSelect = new Map<number, number>();
        frameCartItems.forEach(cartItem => {
            const productIndex = frameProductsMemo.findIndex(p => String(p.id) === String(cartItem.id));
            if (productIndex !== -1 && !currentSelected[productIndex]) {
                indicesToSelect.set(productIndex, cartItem.quantity);
            }
        });

        if (indicesToSelect.size > 0) {
            setSelectedFrames((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToSelect.forEach((quantity, index) => {
                    if (updated[index] === undefined) {
                        updated[index] = 1;
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });

            setSelectedFramesCount((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                indicesToSelect.forEach((quantity, index) => {
                    if (updated[index] !== quantity) {
                        updated[index] = quantity;
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }
    }, [cartItems, frameProductsMemo]);


    // Fetch products → generate mockups → render.
    // Back from qr-scan-to-pay restores session cache immediately (no generate / product API).
    useEffect(() => {
        let cancelled = false;
        const art = typeof currentArt === "string" ? currentArt.trim() : "";
        const artKey = normalizeCartArtKey(art);

        const applyReadyMerchandise = (
            catalog: Product[],
            withMockups: Product[],
            nextPacks?: Pack[]
        ) => {
            catalogMerchandiseRef.current = catalog;
            generatedMerchandiseRef.current = withMockups;
            merchGenerateDoneKeyRef.current = `${artKey}::${catalog
                .map((p) => String(p.id))
                .join(",")}`;
            if (nextPacks) setPacks(nextPacks);
            setMerchandiseProducts(withMockups);
            setIsLoading(false);
            if (artKey) {
                writePrintFrameMerchPageCache({
                    artKey,
                    catalog,
                    merchandise: withMockups,
                    packs: nextPacks || [],
                });
            }
        };

        // Instant restore when returning from payment QR (or remount).
        if (artKey) {
            const pageCache = readPrintFrameMerchPageCache(artKey);
            if (pageCache?.merchandise?.length) {
                catalogMerchandiseRef.current = pageCache.catalog || [];
                generatedMerchandiseRef.current = pageCache.merchandise;
                merchGenerateDoneKeyRef.current = `${artKey}::${(
                    pageCache.catalog || []
                )
                    .map((p) => String(p.id))
                    .join(",")}`;
                setPacks((pageCache.packs as Pack[]) || []);
                setMerchandiseProducts(pageCache.merchandise);
                setIsLoading(false);
                setError(null);
                return;
            }
        }

        const loadAndGenerate = async () => {
            setIsLoading(true);
            setError(null);
            setMerchandiseProducts([]);

            try {
                let merchandise = catalogMerchandiseRef.current;
                let nextPacks: Pack[] | undefined;

                // Caricature portrait: always refresh /product/all before merch generate.
                const shouldFetchProducts =
                    isCaricaturePortrait || merchandise.length === 0;

                if (shouldFetchProducts) {
                    const productsResponse = await fetchAllProducts({
                        forceRefresh: isCaricaturePortrait,
                    });
                    if (cancelled) return;

                    if (!productsResponse.success || !productsResponse.data) {
                        setError(productsResponse.error || "Failed to load products");
                        catalogMerchandiseRef.current = [];
                        setPacks([]);
                        setIsLoading(false);
                        return;
                    }

                    const allProducts = productsResponse.data || [];
                    merchandise = allProducts.filter(
                        (product) => product.type === "instore-merchandise"
                    );
                    nextPacks = enrichPacksWithCatalogProducts(
                        mapProductsToPacks(allProducts),
                        allProducts
                    );
                    catalogMerchandiseRef.current = merchandise;
                    if (!cancelled) setPacks(nextPacks);

                    if (merchandise.length === 0 && nextPacks.length === 0) {
                        setError("No products available");
                        setIsLoading(false);
                        return;
                    }
                }

                if (merchandise.length === 0) {
                    setMerchandiseProducts([]);
                    setIsLoading(false);
                    return;
                }

                // No artwork yet — keep loader; effect re-runs when currentArt arrives.
                if (!art) return;

                const requestKey = `${artKey}::${merchandise
                    .map((p) => String(p.id))
                    .join(",")}`;

                if (
                    merchGenerateDoneKeyRef.current === requestKey &&
                    generatedMerchandiseRef.current.length > 0
                ) {
                    applyReadyMerchandise(
                        merchandise,
                        generatedMerchandiseRef.current,
                        nextPacks
                    );
                    return;
                }

                // Caricature portrait → is_transparent=true for merch generate.
                // TEMP test: already_transparent=false only for Pets (not People).
                const merchIsTransparent = isCaricaturePortrait
                    ? true
                    : readFavouriteTransparencyFlag();
                const merchAlreadyTransparent = !isPortraitPetSubject;

                // Sync session cache — paint immediately, skip generate network call.
                const cachedGenerate = getCachedMerchGenerateResponse(
                    art,
                    merchandise,
                    "merch",
                    merchIsTransparent,
                    merchAlreadyTransparent
                );
                if (cachedGenerate?.items?.length) {
                    const withMockups = applyMerchGenerateItemsToProducts(
                        merchandise,
                        cachedGenerate.items
                    );
                    if (!cancelled) {
                        applyReadyMerchandise(merchandise, withMockups, nextPacks);
                    }
                    return;
                }

                const result = await generateMerchMockups({
                    artwork: art,
                    products: merchandise,
                    station: "merch",
                    isTransparent: merchIsTransparent,
                    alreadyTransparent: merchAlreadyTransparent,
                });
                if (cancelled) return;

                if (result.success && result.items?.length) {
                    const withMockups = applyMerchGenerateItemsToProducts(
                        merchandise,
                        result.items
                    );
                    applyReadyMerchandise(merchandise, withMockups, nextPacks);
                } else {
                    generatedMerchandiseRef.current = merchandise;
                    setMerchandiseProducts(merchandise);
                    setIsLoading(false);
                }
            } catch (err: any) {
                if (cancelled) return;
                setError(err?.message || "An error occurred while loading products");
                catalogMerchandiseRef.current = [];
                generatedMerchandiseRef.current = [];
                setMerchandiseProducts([]);
                setPacks([]);
            } finally {
                if (!cancelled && art) setIsLoading(false);
            }
        };

        loadAndGenerate();
        return () => {
            cancelled = true;
        };
    }, [currentArt, isCaricaturePortrait, isPortraitPetSubject]);

    useEffect(() => {
        if (!packs.length) return;

        const packCartItems = cartItems.filter(
            (item) =>
                (item.isPack || String(item.type || "").toLowerCase() === "pack") &&
                packs.some((pack) => String(pack.id) === String(item.productId || item.id)) &&
                normalizeCartArtKey(item.finalCanvasImage) === normalizeCartArtKey(currentArt)
        );

        const nextSelected: { [key: number]: number } = {};
        const nextCounts: { [key: number]: number } = {};

        packCartItems.forEach((cartItem) => {
            const packIndex = packs.findIndex(
                (pack) => String(pack.id) === String(cartItem.productId || cartItem.id)
            );
            if (packIndex === -1) return;
            nextSelected[packIndex] = 1;
            nextCounts[packIndex] = cartItem.quantity || 1;
        });

        setSelectedPacks(nextSelected);
        setSelectedPacksCount(nextCounts);
    }, [cartItems, packs, currentArt]);

    const armModalClickGuard = useCallback(() => {
        suppressProductOpenUntilRef.current = Date.now() + 400;
        setBlockProductPointer(true);

        if (blockProductPointerTimerRef.current) {
            clearTimeout(blockProductPointerTimerRef.current);
        }
        blockProductPointerTimerRef.current = setTimeout(() => {
            setBlockProductPointer(false);
            blockProductPointerTimerRef.current = null;
        }, 400);
    }, []);

    const isProductOpenSuppressed = useCallback(
        () => Date.now() < suppressProductOpenUntilRef.current,
        []
    );

    const handleMerchSelect = useCallback((index: number, product?: Product) => {
        if (!product || isProductOpenSuppressed()) return;

        const existingCartItem = findMerchCartItemForProduct(product, cartItems, currentArt);
        const quantity = existingCartItem?.quantity ?? 1;

        setMerchandiseModal({
            show: true,
            item: product,
            index: index,
            initialQuantity: quantity,
        });
    }, [cartItems, currentArt, isProductOpenSuppressed]);

    const handlePackSelect = useCallback((index: number, pack: Pack) => {
        if (!pack || isProductOpenSuppressed()) return;

        const existingCartItem = cartItems.find(
            (item) =>
                String(item.productId || item.id) === String(pack.id) &&
                (item.isPack || item.type === resolveCartItemType(pack.type, "pack")) &&
                normalizeCartArtKey(item.finalCanvasImage) === normalizeCartArtKey(currentArt)
        );

        setPackModal({
            show: true,
            pack,
            index,
            initialQuantity: existingCartItem?.quantity || 1,
        });
    }, [cartItems, currentArt, isProductOpenSuppressed]);

    const handleClosePackModal = useCallback(() => {
        armModalClickGuard();
        setPackModal({
            show: false,
            pack: null,
            index: -1,
            initialQuantity: 1,
        });
    }, [armModalClickGuard]);

    const handlePackConfirm = useCallback((
        quantity: number,
        selections: Record<string, PackProductSelection> = {}
    ) => {
        const pack = packModal.pack;
        const index = packModal.index;
        if (!pack || quantity <= 0) return;

        const price =
            getPackUnitTotalFromSelections(pack, selections) || getPackDisplayPrice(pack);
        const packImage = getPackDisplayImage(pack, PackOne);

        dispatch(
            upsertCartItem({
                id: pack.id,
                productId: pack.id,
                name: typeof pack.name === "string" ? pack.name : String(pack.name || "Pack"),
                type: resolveCartItemType(pack.type, "pack"),
                quantity,
                price,
                imageUrl: packImage,
                imgURL: packImage,
                finalCanvasImage: currentArt || undefined,
                sku_code: pack.sku_code || "pack",
                isPack: true,
                products: mapPackProductsForCart(pack, quantity, selections),
            })
        );

        if (index >= 0) {
            setSelectedPacks((prev) => ({ ...prev, [index]: 1 }));
            setSelectedPacksCount((prev) => ({
                ...prev,
                [index]: quantity,
            }));
        }
    }, [currentArt, dispatch, packModal.index, packModal.pack]);

    const handleMerchIncrease = useCallback((index: number) => {
        setSelectedMerchandiseCount((prev) => {
            const product = merchandiseProductsMemo?.[index];
            if (!product) return prev;

            const defaultColor =
                findPreferredProductColor(product.color, product) || product.color?.[0] || null;
            const defaultSize = findPreferredProductSize(product, defaultColor);
            const stock = getAvailableStock(product, {
                size: defaultSize,
                color: defaultColor,
            });
            const current = prev[index] ?? 1;
            if (stock > 0 && current >= stock) return prev;
            // Allow add when any size/color is in stock (not only preferred color).
            if (!isProductInStock(product)) {
                return prev;
            }

            const newCount = current + 1;
            const updated = { ...prev, [index]: newCount };
            const existingCartItem = findMerchCartItemForProduct(product, cartItems, currentArt);

            if (existingCartItem) {
                dispatch(
                    updateCartItemQuantity({
                        id: existingCartItem.id,
                        type: resolveCartItemType(existingCartItem.type || product.type),
                        quantity: newCount,
                        size: existingCartItem.size,
                        variety: existingCartItem.variety,
                        color: existingCartItem.color,
                        finalCanvasImage: existingCartItem.finalCanvasImage,
                    })
                );
            }

            return updated;
        });
    }, [dispatch, merchandiseProductsMemo, cartItems, currentArt]);

    const handleMerchDecrease = useCallback((index: number) => {
        const current = selectedMerchandiseCountRef.current[index] ?? 1;
        if (current <= 0) return;

        const newCount = current - 1;
        setSelectedMerchandiseCount((prev) => ({ ...prev, [index]: newCount }));

        if (newCount === 0) {
            setSelectedMerchandise((prev) => {
                const updated = { ...prev };
                delete updated[index];
                return updated;
            });
        }

        const product = merchandiseProductsMemo?.[index];
        if (!product) return;

        const existingCartItem = findMerchCartItemForProduct(product, cartItems, currentArt);
        if (!existingCartItem) return;

        if (newCount === 0) {
            dispatch(
                removeFromCart({
                    id: existingCartItem.id,
                    type: resolveCartItemType(existingCartItem.type || product.type),
                    size: existingCartItem.size,
                    variety: existingCartItem.variety,
                    color: existingCartItem.color,
                    finalCanvasImage: existingCartItem.finalCanvasImage,
                })
            );
            return;
        }

        dispatch(
            updateCartItemQuantity({
                id: existingCartItem.id,
                type: resolveCartItemType(existingCartItem.type || product.type),
                quantity: newCount,
                size: existingCartItem.size,
                variety: existingCartItem.variety,
                color: existingCartItem.color,
                finalCanvasImage: existingCartItem.finalCanvasImage,
            })
        );
    }, [dispatch, merchandiseProductsMemo, cartItems, currentArt]);

    const totalCartItems = useMemo(() => cartItems.length, [cartItems.length]);

    const cartSubtotal = useMemo(() => {
        return (cartItems || []).reduce((sum, item) => {
            const price = typeof item.price === "number" ? item.price : parseFloat(String(item.price || "0"));
            return sum + price * (item.quantity || 0);
        }, 0);
    }, [cartItems]);

    const cartPricing = useMemo(
        () =>
            calculateMuseumFeeTaxTotals(cartSubtotal, {
                processingFeePercent,
                salesTaxPercent,
            }),
        [cartSubtotal, processingFeePercent, salesTaxPercent]
    );

    const cartTotal = cartPricing.total;

    useEffect(() => {
        if (museumLoaded || museumLoading) return;
        dispatch(loadMuseumFeeTax());
    }, [dispatch, museumLoaded, museumLoading]);

    const handleShowCartModal = useCallback(() => {
        setShowCartModal(true);
    }, []);

    /** Pay Now / Pay at Counter require login first. */
    const requireLoginThenCheckout = useCallback(() => {
        if (cartItems.length === 0 || isProcessingPayment) return;

        const loggedIn = Boolean(authUser ?? getStoredAuthUser());
        if (loggedIn) {
            setShowCartModal(true);
            return;
        }

        pendingCheckoutAfterLoginRef.current = true;
        pendingHomeAfterSaveLoginRef.current = false;
        setShowSigninSkipButton(false);
        setSigninModal(true);
    }, [authUser, cartItems.length, isProcessingPayment]);

    const buildPackCheckoutPayload = useCallback((guestEmail?: string) => {
        const session = authUser ?? getStoredAuthUser();
        const userInfo: Record<string, unknown> = {};
        if (firstName) userInfo.firstName = firstName;
        if (lastName) userInfo.lastName = lastName;
        if (ticketId) userInfo.ticketId = ticketId;
        if (userId) userInfo.userId = userId;
        if (session?.id) userInfo.userId = session.id;
        if (session?.email) userInfo.email = session.email;
        if (session?.name) userInfo.fullName = session.name;
        if (scanResult) {
            if (scanResult.email) userInfo.email = userInfo.email || scanResult.email;
            if (scanResult.phone) userInfo.phone = scanResult.phone;
            if (scanResult.user_id) userInfo.userId = userInfo.userId || scanResult.user_id;
        }
        if (guestEmail) userInfo.email = guestEmail;

        const loggedInUserId = resolvePaymentUserId(session?.id ?? null);

        return {
            items: cartItems.map((item) => {
                const variety = normalizeCartVariety(item.variety);
                // Session final art for tote/apparel; sticker lines use generate mockups below.
                const artUrl = resolveCartItemArtUrl(currentArt);
                // Catalog name wins for detection (single + multi packs share sku ST).
                const catalogProduct = merchandiseProductsMemo?.find(
                    (p) => String(p.id) === String(item.productId || item.id)
                );
                const itemForArt = {
                    ...item,
                    name: catalogProduct?.name ?? item.name,
                    sku_code: catalogProduct?.sku_code ?? item.sku_code,
                    // Prefer live merch/generate mockup from catalog for sticker order images.
                    picture: catalogProduct?.picture,
                    imgURL: catalogProduct?.imgURL ?? item.imgURL,
                    image_url: catalogProduct?.image_url ?? item.imageUrl,
                    imageUrl: item.imageUrl ?? catalogProduct?.image_url,
                    image_url_single_sticker:
                        (item as { image_url_single_sticker?: string })
                          .image_url_single_sticker ||
                        (catalogProduct as { image_url_single_sticker?: string } | undefined)
                          ?.image_url_single_sticker,
                };
                // N-stickers → image_url_single_sticker; single/sheet → own mockup; tote → session art.
                const orderImageUrl = resolveOrderLineImageUrl(itemForArt, artUrl, {
                    singleStickerMockupUrl,
                });

                const base = {
                    id: item.id,
                    productId: item.productId || item.id,
                    productSizeId: item.productSizeId,
                    name: typeof item.name === "string" ? item.name : String(item.name || "Product"),
                    type: item.type,
                    quantity: item.quantity,
                    price: typeof item.price === "number" ? item.price : parseFloat(String(item.price || "0")),
                    size: item.size,
                    color: item.color || variety,
                    variety,
                    sku_code: item.sku_code,
                    image_url: orderImageUrl,
                };

                if (item.isPack || String(item.type || "").toLowerCase() === "pack") {
                    const products =
                        Array.isArray(item.products) && item.products.length > 0
                            ? item.products
                            : [];
                    return {
                        ...base,
                        isPack: true,
                        products: products.map((product) => {
                            const packImageUrl = resolveOrderLineImageUrl(product, artUrl, {
                                singleStickerMockupUrl,
                            });
                            return {
                                id: product.id,
                                productId: product.productId || product.id,
                                productSizeId: product.productSizeId,
                                name: product.name,
                                type: product.type || "product",
                                quantity: product.quantity,
                                price: product.price,
                                unit_price: product.unit_price ?? product.price,
                                size: product.size,
                                inches: product.inches,
                                color: product.color || product.variety,
                                variety: product.variety || product.color,
                                sku_code: product.sku_code,
                                image_url:
                                    packImageUrl ||
                                    (isMultiStickerPackCartItem(product)
                                        ? (product as { image_url_single_sticker?: string })
                                            .image_url_single_sticker ||
                                          singleStickerMockupUrl
                                        : product.imageUrl || product.picture),
                                units: product.units,
                            };
                        }),
                    };
                }

                return base;
            }),
            totalAmount: parseFloat(cartTotal.toFixed(2)),
            currency: "USD",
            subtotal: cartPricing.subtotal,
            processingFee: cartPricing.processingFee,
            salesTax: cartPricing.salesTax,
            processingFeePercent: cartPricing.processingFeePercent,
            salesTaxPercent: cartPricing.salesTaxPercent,
            paymentMethod: undefined as string | undefined,
            status: undefined as string | undefined,
            // Top-level userId is what generate-payment-link reads for the QR URL.
            ...(loggedInUserId ? { userId: loggedInUserId, user_id: loggedInUserId } : {}),
            userInfo: Object.keys(userInfo).length > 0 ? userInfo : undefined,
        };
    }, [
        authUser,
        cartItems,
        cartPricing.processingFee,
        cartPricing.processingFeePercent,
        cartPricing.salesTax,
        cartPricing.salesTaxPercent,
        cartPricing.subtotal,
        cartTotal,
        currentArt,
        firstName,
        merchandiseProductsMemo,
        lastName,
        scanResult,
        singleStickerMockupUrl,
        ticketId,
        userId,
    ]);

    const handlePackCheckout = useCallback(async () => {
        if (cartItems.length === 0) {
            return;
        }

        setIsProcessingPayment(true);
        setShowCartModal(false);
        setShowGuestEmailModal(false);

        try {
            const payload = buildPackCheckoutPayload();
            console.log("payload", payload);

            const tempResponse = await createTempData({ data: payload });
            if (!tempResponse.success || !tempResponse.data?.id) {
                throw new Error(tempResponse.error || "Failed to create temp data");
            }

            const tempDataId = tempResponse.data.id;
            persistPaymentSessionFromTempData(tempResponse.data);
            dispatch(setTempDataId(tempDataId));

            const orderType = resolvePaymentOrderType(payload.items, "onsite");
            const loggedInUserId = resolvePaymentUserId(
                (authUser ?? getStoredAuthUser())?.id ?? null
            );
            const url = resolvePaymentRedirectUrl(payload.items, {
                paymentType: "onsite",
                userId: loggedInUserId,
            });
            const response = await scanQRPaymentAPI(tempDataId, {
                url,
                type: orderType,
                data: loggedInUserId ? { userId: loggedInUserId, user_id: loggedInUserId } : undefined,
            });

            if (!response.success || !response.data) {
                throw new Error(response.error || "Failed to generate payment QR code");
            }

            let qrCodeUrl: string;
            if (typeof response.data === "string") {
                qrCodeUrl = response.data;
            } else if (typeof response.data === "object") {
                qrCodeUrl = response.data.qrCodeUrl || response.data.qrCode || response.data.paymentLink || "";
            } else {
                throw new Error("Invalid QR code URL format");
            }

            if (!qrCodeUrl || !(qrCodeUrl.startsWith("http://") || qrCodeUrl.startsWith("https://"))) {
                throw new Error("QR code URL not found in response");
            }

            dispatch(setPaymentQR(qrCodeUrl));
            const themedStation = enrichStationWithThemeBackgrounds(stationData, selectedArt);
            persistStationSelection(themedStation);
            const stationSlug = resolveStationRouteSlug(themedStation, undefined, selectedArt as any);
            delayedNavigate(`/${stationSlug}/qr-scan-to-pay`, {
                state: {
                    stationData: themedStation,
                    snapshot: currentArt,
                    selectedArt,
                    paymentType: orderType,
                    previousRoute: `/${stationSlug}/print-frame-selection`,
                    totalAmount: cartTotal,
                },
                delay: 500,
            });
        } catch (error: any) {
            console.error("Payment Error:", error);
            toast.error(error?.message || "Failed to generate payment QR code. Please try again.");
        } finally {
            setIsProcessingPayment(false);
        }
    }, [
        authUser,
        buildPackCheckoutPayload,
        cartItems.length,
        cartTotal,
        currentArt,
        delayedNavigate,
        dispatch,
        selectedArt,
        stationData,
    ]);

    const processPayOnReception = useCallback(async (guestEmail?: string) => {
        if (cartItems.length === 0) {
            return;
        }

        setIsProcessingPayment(true);
        setShowCartModal(false);

        try {
            const payload = buildPackCheckoutPayload(guestEmail);
            payload.paymentMethod = "pay_on_reception";
            payload.status = "payment-pending";

            const tempResponse = await createTempData({ data: payload });
            if (!tempResponse.success || !tempResponse.data?.id) {
                throw new Error(tempResponse.error || "Failed to create temp data");
            }

            const tempDataId = tempResponse.data.id;
            persistPaymentSessionFromTempData(tempResponse.data);
            dispatch(setTempDataId(tempDataId));

            const orderType = resolvePaymentOrderType(payload.items, "onsite");
            const orderResponse = await createPayOnReceptionOrder(
                {
                    temp_data_id: tempDataId,
                    email: guestEmail || (payload.userInfo?.email as string | undefined),
                },
                orderType
            );

            if (!orderResponse.success || !orderResponse.data?.length) {
                throw new Error(orderResponse.error || "Failed to place pay-on-reception order");
            }

            const session = savePendingPaymentOrderFromApiOrders(
                orderResponse.data as Array<Record<string, unknown>>,
                tempDataId
            );
            dispatch(clearCart());
            setShowGuestEmailModal(false);

            const themedStation = enrichStationWithThemeBackgrounds(stationData, selectedArt);
            persistStationSelection(themedStation);
            const stationSlug = resolveStationRouteSlug(themedStation, undefined, selectedArt as any);
            const orderNumbers = session.orderNumbers || (session.orderNumber ? [session.orderNumber] : []);

            delayedNavigate(`/${stationSlug}/order-confirmation`, {
                state: {
                    stationData: themedStation,
                    snapshot: currentArt,
                    selectedArt,
                    orderNumbers,
                    orderNumber: orderNumbers[0],
                    paymentMethod: "pay_on_reception",
                },
                delay: 400,
            });
        } catch (error: any) {
            console.error("Pay on reception error:", error);
            toast.error(error?.message || "Failed to place order. Please try again.");
        } finally {
            setIsProcessingPayment(false);
        }
    }, [
        buildPackCheckoutPayload,
        cartItems.length,
        currentArt,
        delayedNavigate,
        dispatch,
        selectedArt,
        stationData,
    ]);

    const handlePackPayOnReception = useCallback(() => {
        if (cartItems.length === 0) {
            return;
        }

        // Confirm from cart modal → collect user email, then place pay-at-counter order.
        setShowCartModal(false);
        setShowGuestEmailModal(true);
    }, [cartItems.length]);

    const handleGuestEmailSubmit = useCallback(
        async (email: string) => {
            await processPayOnReception(email);
        },
        [processPayOnReception]
    );

    const navigateAfterSkip = useCallback(() => {
        const artForNav = selectedArt || readSelectedAiSelfieArtFromStorage();
        const activeStation =
            (stationData && Object.keys(stationData).length > 0
                ? stationData
                : readActiveStoredStation()) || undefined;
        const themedStation = enrichStationWithThemeBackgrounds(activeStation, artForNav);
        if (themedStation?.type?.trim()) {
            persistStationSelection(themedStation);
        }
        const stationSlug =
            slugParam?.trim() ||
            resolveStationRouteSlug(themedStation, slugParam, artForNav as any);
        navigate(`/${stationSlug || "hobby-caricature"}/thank-you`, {
            state: {
                stationData: themedStation,
                selectedArt: artForNav,
            },
        });
    }, [navigate, selectedArt, slugParam, stationData]);

    const handleCloseSuccessModal = useCallback(() => {
        armModalClickGuard();
        setSuccessModal(false);
    }, [armModalClickGuard]);

    const handleClosePrintSizeModal = useCallback(() => {
        armModalClickGuard();
        setPrintSizeModal(false);
    }, [armModalClickGuard]);

    const handleCloseCartModal = useCallback(() => {
        armModalClickGuard();
        setShowCartModal(false);
    }, [armModalClickGuard]);

    const handleCloseMerchandiseModal = useCallback(() => {
        armModalClickGuard();
        setMerchandiseModal({ show: false, item: {} as Product, index: -1, initialQuantity: 0 });
    }, [armModalClickGuard]);

    const handleMerchandiseAddedToCart = useCallback(() => {
        if (merchandiseModal.index === -1) return;

        const product = merchandiseProductsMemo?.[merchandiseModal.index];
        if (!product) return;

        const cartItem = findMerchCartItemForProduct(product, cartItems, currentArt);
        const quantity = cartItem?.quantity ?? 0;

        setSelectedMerchandise((prev) => {
            if (quantity <= 0) {
                const updated = { ...prev };
                delete updated[merchandiseModal.index];
                return updated;
            }
            return { ...prev, [merchandiseModal.index]: 1 };
        });
        setSelectedMerchandiseCount((prev) => ({
            ...prev,
            [merchandiseModal.index]: quantity,
        }));
    }, [cartItems, currentArt, merchandiseModal.index, merchandiseProductsMemo]);


    const hobbyPersonCompletedResults = useSelector(
        (s: RootState) => s.uploadFlow?.hobbyPersonCompletedResults ?? []
    );
    const favouritesState = useSelector((s: RootState) => s.favourites);
    const favourites = favouritesState.items;
    const favouritesHasMore = favouritesState.hasMore;
    const favouritesLoadingMore = favouritesState.isLoadingMore;
    const sessionUser = useMemo(
        () => authUser ?? getStoredAuthUser(),
        [authUser, location.key]
    );
    const isLoggedIn = Boolean(sessionUser?.id);
    const displayName = sessionUser?.name || sessionUser?.email || "Signed in";

    // No API call here – favourites are loaded into Redux elsewhere (e.g. MyFavouritesList)

    useEffect(() => {
        const syncAuthUser = () => {
            setAuthUser(getStoredAuthUser());
        };

        syncAuthUser();
        window.addEventListener("authUserUpdated", syncAuthUser);
        window.addEventListener("pageshow", syncAuthUser);

        return () => {
            window.removeEventListener("authUserUpdated", syncAuthUser);
            window.removeEventListener("pageshow", syncAuthUser);
        };
    }, [location.key]);

    useEffect(() => {
        let cancelled = false;

        const loadFavourites = async () => {
            if (!sessionUser?.id) {
                lastFavouritesUserIdRef.current = null;
                dispatch(clearFavourites());
                dispatch(setFavouritesError(null));
                return;
            }

            // Sign-in persists the user before onAuthSuccess; skip while modal/handler owns sync.
            if (signinModal || signupModal || authFavouritesSyncRef.current) return;
            if (lastFavouritesUserIdRef.current === sessionUser.id) return;

            dispatch(startLoadingFavourites({ userId: sessionUser.id }));
            const response = await getUserFavourites(sessionUser.id, {
                page: 1,
                limit: FAVOURITES_PAGE_SIZE,
                accessToken: resolveAuthAccessToken(sessionUser),
            });

            if (cancelled || authFavouritesSyncRef.current) return;

            if (response.success) {
                lastFavouritesUserIdRef.current = sessionUser.id;
                dispatch(setFavouritesPage({
                    items: response.data || [],
                    page: response.pagination?.page || 1,
                    hasMore: Boolean(response.pagination?.hasMore),
                }));
                return;
            }

            dispatch(setFavouritesError(response.error || "Failed to load favourites"));
        };

        loadFavourites();
        return () => {
            cancelled = true;
        };
    }, [sessionUser?.id, dispatch, signinModal, signupModal]);

    const clearMerchandiseSelection = useCallback(() => {
        setSelectedMerchandise({});
        setSelectedMerchandiseCount({});
    }, []);

    /** Logout → show success modal → navigate home (used for every leave-to-/ while logged in). */
    const logoutThenNavigateHome = useCallback(() => {
        setShowSaveArtBackModal(false);
        setShowBackConfirmModal(false);
        setShowSignOutConfirmModal(false);
        setSigninModal(false);
        setSignupModal(false);
        pendingHomeAfterSaveLoginRef.current = false;
        pendingNavigateHomeAfterLogoutRef.current = true;
        clearStoredAuthUser();
        setAuthUser(null);
        lastFavouritesUserIdRef.current = null;
        dispatch(clearFavourites());
        setSelectedFavouriteUrl(null);
        clearMerchandiseSelection();
        setShowFavouriteModal(false);
        setLogoutSuccessModal(true);
    }, [clearMerchandiseSelection, dispatch]);

    const handleAuthSuccess = useCallback(async (user: AuthUser) => {
        authFavouritesSyncRef.current = true;
        persistAuthUser(user);
        setAuthUser(user);
        setSigninModal(false);
        setSignupModal(false);
        setForgotPasswordModal(false);
        setShowSigninSkipButton(false);
        setSaveArtSuccess(false);
        setIsSavingFavourites(false);

        const routeCharacterUrls = (location.state as { hobbyCharacterFavouriteUrls?: string[] } | null)
            ?.hobbyCharacterFavouriteUrls;
        const fallbackCharacterUrls = (hobbyPersonCompletedResults || []).filter(
            (url): url is string => typeof url === "string" && url.trim().length > 0
        );
        // Back → Yes → login: art-save loader (~3s) → logout success → navigate home.
        const fromBackSaveFlow = pendingHomeAfterSaveLoginRef.current;
        pendingHomeAfterSaveLoginRef.current = false;
        const fromCheckoutLogin = pendingCheckoutAfterLoginRef.current;
        // Keep checkout pending until we open cart (or clear on back-save home exit).
        const artUrl = typeof currentArt === "string" ? currentArt.trim() : "";
        const accessToken = resolveAuthAccessToken(user);

        try {
            // One page=1 fetch for check + save + Redux (avoid 3–4 duplicate calls on login).
            dispatch(startLoadingFavourites({ userId: user.id }));
            const favouritesResponse = await getUserFavourites(user.id, {
                page: 1,
                limit: FAVOURITES_PAGE_SIZE,
                accessToken,
            });
            const loadedFavourites = favouritesResponse.success
                ? favouritesResponse.data || []
                : [];
            if (favouritesResponse.success) {
                lastFavouritesUserIdRef.current = user.id;
                dispatch(setFavouritesPage({
                    items: loadedFavourites,
                    page: favouritesResponse.pagination?.page || 1,
                    hasMore: Boolean(favouritesResponse.pagination?.hasMore),
                }));
            } else {
                dispatch(setFavouritesError(
                    favouritesResponse.error || "Failed to load favourites"
                ));
            }

            const favouriteCheck = await checkFavouriteImageAlreadySaved({
                userId: user.id,
                imageUrl: artUrl,
                accessToken,
                favourites: loadedFavourites,
            });
            const willSaveArtToFavourite = favouriteCheck.shouldCreate;
            // Back → Yes → login: always show save modal after login (even if API skips duplicate).
            // Toolbar login: only show modal when a new favourite is created.
            const showSaveLoaderModal = fromBackSaveFlow || willSaveArtToFavourite;
            const saveLoaderMs = fromBackSaveFlow
                ? minBackSaveLoaderDurationMs
                : minSaveLoaderDurationMs;

            if (showSaveLoaderModal) {
                setIsSavingFavourites(true);
            }

            const minimumDelay = showSaveLoaderModal
                ? new Promise<void>((resolve) =>
                    window.setTimeout(resolve, saveLoaderMs)
                )
                : Promise.resolve();

            const [, saveResult] = await Promise.all([
                minimumDelay,
                saveArtAndHobbyFavouritesAfterLogin({
                    user,
                    artUrl: currentArt,
                    ticketId: ticketId || undefined,
                    routeCharacterUrls,
                    fallbackCharacterUrls,
                    existingFavourites: loadedFavourites,
                }),
            ]);

            if (!saveResult.success) {
                setIsSavingFavourites(false);
                window.alert(saveResult.error || "Failed to save your favourite. Please try again.");
                return;
            }

            // Refresh list only when something new was created.
            if (saveResult.createdArt || saveResult.createdBulk) {
                const response = await getUserFavourites(user.id, {
                    page: 1,
                    limit: FAVOURITES_PAGE_SIZE,
                    accessToken,
                });
                if (response.success) {
                    lastFavouritesUserIdRef.current = user.id;
                    dispatch(setFavouritesPage({
                        items: response.data || [],
                        page: response.pagination?.page || 1,
                        hasMore: Boolean(response.pagination?.hasMore),
                    }));
                } else {
                    dispatch(setFavouritesError(response.error || "Failed to load favourites"));
                }
            }

            if (fromBackSaveFlow) {
                pendingCheckoutAfterLoginRef.current = false;
                setIsSavingFavourites(false);
                setSaveArtSuccess(false);
                logoutThenNavigateHome();
                return;
            }

            if (showSaveLoaderModal) {
                setIsSavingFavourites(false);
                setSaveArtSuccess(true);
                await new Promise<void>((resolve) => window.setTimeout(resolve, 1200));
                setSaveArtSuccess(false);
            }
        } finally {
            setIsSavingFavourites(false);
            authFavouritesSyncRef.current = false;
            // Pay Now / Pay at Counter → login → reopen cart (even if favourite save skipped).
            if (fromCheckoutLogin && pendingCheckoutAfterLoginRef.current) {
                pendingCheckoutAfterLoginRef.current = false;
                setShowCartModal(true);
            }
        }
    }, [
        currentArt,
        dispatch,
        hobbyPersonCompletedResults,
        location.state,
        logoutThenNavigateHome,
        ticketId,
    ]);

    const handleLoadMoreFavourites = useCallback(async () => {
        if (!sessionUser?.id || favouritesState.isLoadingMore || !favouritesState.hasMore) return;

        const nextPage = (favouritesState.page || 1) + 1;
        dispatch(startLoadingMoreFavourites());
        const response = await getUserFavourites(sessionUser.id, {
            page: nextPage,
            limit: FAVOURITES_PAGE_SIZE,
            accessToken: resolveAuthAccessToken(sessionUser),
        });

        if (response.success) {
            dispatch(appendFavourites({
                items: response.data || [],
                page: response.pagination?.page || nextPage,
                hasMore: Boolean(response.pagination?.hasMore),
            }));
            return;
        }

        dispatch(setFavouritesError(response.error || "Failed to load favourites"));
    }, [
        dispatch,
        favouritesState.hasMore,
        favouritesState.isLoadingMore,
        favouritesState.page,
        sessionUser,
    ]);

    const handleGoogleAuth = useCallback(async () => {
        await authService.googleAuth();
    }, []);

    const handleLogout = useCallback(() => {
        armModalClickGuard();
        setShowSignOutConfirmModal(false);
        clearStoredAuthUser();
        setAuthUser(null);
        lastFavouritesUserIdRef.current = null;
        dispatch(clearFavourites());
        setSelectedFavouriteUrl(null);
        clearMerchandiseSelection();
        setShowFavouriteModal(false);
        setLogoutSuccessModal(true);
    }, [armModalClickGuard, dispatch, clearMerchandiseSelection]);

    const openSaveArtBeforeLeaveModal = useCallback(() => {
        setShowBackConfirmModal(false);
        setShowSaveArtBackModal(false);
        pendingHomeAfterSaveLoginRef.current = false;
        pendingNavigateHomeAfterLogoutRef.current = false;

        const loggedIn = Boolean(authUser ?? getStoredAuthUser());
        const artUrl = typeof currentArt === "string" ? currentArt.trim() : "";
        const isAlreadySavedFlag = readFavouriteIsAlreadySaved();
        const artAlreadySaved =
            Boolean(artUrl) &&
            (isFavouriteArtSavedLocally(artUrl) ||
                isFavouriteImageInList(artUrl, favourites) ||
                isAlreadySavedFlag);

        // Home confirm (not save-art prompt) when:
        // - logged in + cart or art already saved
        // - guest + isAlreadySaved true (empty cart → homepage msg; with cart → cart-loss msg)
        if (
            (loggedIn && (cartItems.length > 0 || artAlreadySaved)) ||
            (!loggedIn && (artAlreadySaved || isAlreadySavedFlag))
        ) {
            setShowBackConfirmModal(true);
            return;
        }

        setShowSaveArtBackModal(true);
    }, [authUser, cartItems.length, currentArt, favourites]);

    const handleSkipPackSelection = useCallback(() => {
        setShowSkipConfirmModal(true);
    }, []);

    const handleSkipConfirmYes = useCallback(() => {
        setShowSkipConfirmModal(false);
        const loggedIn = Boolean(authUser ?? getStoredAuthUser());
        if (loggedIn) {
            // Logout → success modal → thank-you
            setPendingSkipAfterLogout(true);
            pendingNavigateHomeAfterLogoutRef.current = false;
            clearStoredAuthUser();
            setAuthUser(null);
            dispatch(clearFavourites());
            setSelectedFavouriteUrl(null);
            clearMerchandiseSelection();
            setShowFavouriteModal(false);
            setLogoutSuccessModal(true);
            return;
        }
        navigateAfterSkip();
    }, [
        authUser,
        clearMerchandiseSelection,
        dispatch,
        navigateAfterSkip,
    ]);

    const handleLogoutSuccessHide = useCallback(() => {
        setLogoutSuccessModal(false);
        if (pendingNavigateHomeAfterLogoutRef.current) {
            pendingNavigateHomeAfterLogoutRef.current = false;
            navigate("/", { replace: true });
            return;
        }
        if (pendingSkipAfterLogout) {
            setPendingSkipAfterLogout(false);
            navigateAfterSkip();
        }
    }, [navigate, navigateAfterSkip, pendingSkipAfterLogout]);

    const handleSignOutCancel = useCallback(() => {
        armModalClickGuard();
        setPendingSkipAfterLogout(false);
        setShowSignOutConfirmModal(false);
    }, [armModalClickGuard]);

    const handleFavouriteSelect = useCallback((favourite: Favourite) => {
        setSelectedFavouriteUrl(favourite.imageUrl);
        clearMerchandiseSelection();
    }, [clearMerchandiseSelection]);

    const goToHomeFromBack = useCallback(() => {
        setShowSaveArtBackModal(false);
        setShowBackConfirmModal(false);
        const loggedIn = Boolean(authUser ?? getStoredAuthUser());
        if (loggedIn) {
            logoutThenNavigateHome();
            return;
        }
        pendingHomeAfterSaveLoginRef.current = false;
        pendingNavigateHomeAfterLogoutRef.current = false;
        navigate("/", { replace: true });
    }, [authUser, logoutThenNavigateHome, navigate]);

    const handleBackSaveArtYes = useCallback(() => {
        setShowSaveArtBackModal(false);
        // Login → art save loader (~3s) → logout success → /.
        pendingHomeAfterSaveLoginRef.current = true;
        const user = authUser || getStoredAuthUser();
        if (user) {
            void handleAuthSuccess(user);
            return;
        }
        setShowSigninSkipButton(true);
        setSigninModal(true);
    }, [authUser, handleAuthSuccess]);

    const handleBackSaveArtNo = useCallback(() => {
        setShowSaveArtBackModal(false);
        setShowBackConfirmModal(true);
    }, []);

    const isOverlayOpen =
        showFavouriteModal ||
        signinModal ||
        signupModal ||
        forgotPasswordModal ||
        successModal ||
        logoutSuccessModal ||
        showSignOutConfirmModal ||
        showSaveArtBackModal ||
        showBackConfirmModal ||
        showSkipConfirmModal ||
        isSavingFavourites ||
        saveArtSuccess ||
        printSizeModal ||
        showCartModal ||
        merchandiseModal.show ||
        packModal.show;

    useEffect(() => {
        if (wasOverlayOpenRef.current && !isOverlayOpen) {
            armModalClickGuard();
        }
        wasOverlayOpenRef.current = isOverlayOpen;
    }, [armModalClickGuard, isOverlayOpen]);

    useEffect(() => {
        return () => {
            if (blockProductPointerTimerRef.current) {
                clearTimeout(blockProductPointerTimerRef.current);
            }
        };
    }, []);

    console.log("cartItems", currentArt);

    return (
        <>
            {(isLoading || isProcessingPayment) && <Loader />}
            {(isSavingFavourites || saveArtSuccess) && (
                <LoaderSaveModal
                    show={isSavingFavourites || saveArtSuccess}
                    loading={isSavingFavourites}
                    success={saveArtSuccess}
                    onHide={() => {
                        setIsSavingFavourites(false);
                        setSaveArtSuccess(false);
                    }}
                />
            )}
            <StationThemeLayout
                className="eCommercePrintSection printFrameSelectionPage packSelectionPage"
                station={stationData}
                onBack={openSaveArtBeforeLeaveModal}
            >
                <header className="packSelectionHeader">
                    <div className="cartSide packSelectionActions">
                        <div className="packSelectionToolbar" role="group" aria-label="Account actions">
                            {isLoggedIn && (
                                <>
                                    <button
                                        type="button"
                                        className="packSelectionToolbarBtn"
                                        aria-label="My gallery"
                                        title="My gallery"
                                        onClick={() => setShowFavouriteModal(true)}
                                    >
                                        <Heart />
                                        {favourites.length > 0 && (
                                            <span className="packSelectionToolbarBadge packSelectionToolbarBadgeFavourite">
                                                {favourites.length}
                                            </span>
                                        )}
                                    </button>
                                    <span className="packSelectionToolbarDivider" aria-hidden="true" />
                                </>
                            )}

                            {isLoggedIn ? (
                                <button
                                    type="button"
                                    className="packSelectionToolbarBtn"
                                    aria-label={`Signed in as ${displayName}. Log out`}
                                    title={displayName}
                                    onClick={() => {
                                        setPendingSkipAfterLogout(false);
                                        setShowSignOutConfirmModal(true);
                                    }}
                                >
                                    <User />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="packSelectionToolbarBtn"
                                    aria-label="Log in"
                                    title="Log in"
                                    onClick={() => {
                                        setShowSigninSkipButton(false);
                                        pendingHomeAfterSaveLoginRef.current = false;
                                        setSigninModal(true);
                                    }}
                                >
                                    <LogIn />
                                </button>
                            )}

                            <span className="packSelectionToolbarDivider" aria-hidden="true" />

                            <button
                                type="button"
                                className="packSelectionToolbarBtn"
                                aria-label="View cart"
                                onClick={handleShowCartModal}
                            >
                                <Cart />
                                {totalCartItems > 0 && (
                                    <span className="packSelectionToolbarBadge packSelectionToolbarBadgeCart">
                                        {totalCartItems}
                                    </span>
                                )}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="packSelectionSkipBtn"
                            onClick={handleSkipPackSelection}
                        >
                            Skip <ArrowRight />
                        </button>
                    </div>
                </header>

                <main
                    className={`packSelectionContent${blockProductPointer ? " is-modal-click-guard" : ""}`}
                >
                    {!isLoading && packs.length === 0 && (!merchandiseProductsMemo || merchandiseProductsMemo.length === 0) ? (
                        <div className="packSelectionEmpty">
                            <div className="packSelectionEmptyIcon" aria-hidden="true">
                                {error ? <AlertTriangle size={64} strokeWidth={1.75} /> : <Package size={64} strokeWidth={1.75} />}
                            </div>
                            <div className="packSelectionEmptyTitle">
                                {error ? "Unable to load products" : "No products available"}
                            </div>
                            <div className="packSelectionEmptyText">
                                {error || "Please check back soon or try again later."}
                            </div>
                        </div>
                    ) : (
                        <MerchandiseSelectionList
                            selectedItems={selectedMerchandise}
                            selectedMerchandiseCount={selectedMerchandiseCount}
                            onSelect={handleMerchSelect}
                            onAddmore={(item, index) => {
                                // Open product detail modal instead of quick-adding to cart.
                                if (typeof index === "number") {
                                    handleMerchSelect(index, item);
                                }
                            }}
                            onIncrease={handleMerchIncrease}
                            onDecrease={handleMerchDecrease}
                            products={merchandiseProductsMemo}
                            packs={packs}
                            packSelectedItems={selectedPacks}
                            packSelectedCount={selectedPacksCount}
                            onPackSelect={handlePackSelect}
                            favouriteImg={currentArt}
                            variant="packs"
                        />
                    )}
                </main>

                <footer className="packSelectionFooter">
                    <div className="packSelectionPayActions">
                        <button
                            type="button"
                            className="packSelectionPayBtn"
                            disabled={cartItems.length === 0 || isProcessingPayment}
                            onClick={requireLoginThenCheckout}
                        >
                            Pay Now <ArrowRight />
                        </button>
                        <button
                            type="button"
                            className="packSelectionPayReceptionBtn"
                            disabled={cartItems.length === 0 || isProcessingPayment}
                            onClick={requireLoginThenCheckout}
                        >
                            Pay at Counter
                        </button>
                    </div>
                </footer>
            </StationThemeLayout>

            {showFavouriteModal &&
                <FavouriteArtModal
                    show={showFavouriteModal}
                    onHide={() => setShowFavouriteModal(false)}
                    favourites={favourites}
                    onSelect={handleFavouriteSelect}
                    hasMore={favouritesHasMore}
                    isLoadingMore={favouritesLoadingMore}
                    onLoadMore={handleLoadMoreFavourites}
                />
            }
            {signinModal && (
                <SigninModal
                    show={signinModal}
                    onHide={() => {
                        setSigninModal(false);
                        setShowSigninSkipButton(false);
                        pendingHomeAfterSaveLoginRef.current = false;
                        pendingCheckoutAfterLoginRef.current = false;
                    }}
                    setSignupModal={setSignupModal}
                    setForgotPasswordModal={setForgotPasswordModal}
                    setSigninModal={setSigninModal}
                    onAuthSuccess={handleAuthSuccess}
                    onGoogleAuth={handleGoogleAuth}
                    onSkip={
                        showSigninSkipButton
                            ? () => {
                                setSigninModal(false);
                                setShowSigninSkipButton(false);
                                pendingHomeAfterSaveLoginRef.current = false;
                                setShowBackConfirmModal(true);
                            }
                            : undefined
                    }
                />
            )}
            {signupModal && (
                <SignupModal
                    show={signupModal}
                    onHide={() => {
                        setSignupModal(false);
                        setShowSigninSkipButton(false);
                        pendingHomeAfterSaveLoginRef.current = false;
                    }}
                    setSignupModal={setSignupModal}
                    setSigninModal={setSigninModal}
                    onAuthSuccess={handleAuthSuccess}
                    onGoogleAuth={handleGoogleAuth}
                />
            )}
            {forgotPasswordModal && (
                <ForgotPasswordModal
                    show={forgotPasswordModal}
                    onHide={() => setForgotPasswordModal(false)}
                    setForgotPasswordModal={setForgotPasswordModal}
                    setSigninModal={setSigninModal}
                    setSignupModal={setSignupModal}
                />
            )}
            {successModal && <AddtoCart show={successModal} onHide={handleCloseSuccessModal} />}
            {logoutSuccessModal && (
                <LogoutSuccessModal
                    show={logoutSuccessModal}
                    onHide={handleLogoutSuccessHide}
                />
            )}
            {showSignOutConfirmModal && (
                <SignOutConfirmModal
                    show={showSignOutConfirmModal}
                    onHide={handleSignOutCancel}
                    onConfirm={handleLogout}
                    message="Are you sure you want to log out?"
                />
            )}
            {showSaveArtBackModal && (
                <SignOutConfirmModal
                    show={showSaveArtBackModal}
                    onHide={handleBackSaveArtNo}
                    onConfirm={handleBackSaveArtYes}
                    title={
                        !isLoggedIn && cartItems.length === 0
                            ? "Back to Homepage"
                            : undefined
                    }
                    message="You will lose your art created. Would you like to save your art first?"
                    cancelLabel="No"
                    confirmLabel="Yes"
                />
            )}
            {showBackConfirmModal && (
                <SignOutConfirmModal
                    show={showBackConfirmModal}
                    onHide={() => setShowBackConfirmModal(false)}
                    onConfirm={goToHomeFromBack}
                    message={
                        cartItems.length > 0
                            ? (
                                <>
                                    Your cart items and selections will be lost.
                                    <br />
                                    Are you sure you want to go back to homepage?
                                </>
                            )
                            : "Are you sure you want to go back to homepage?"
                    }
                    cancelLabel="No"
                    confirmLabel="Yes"
                />
            )}
            {showSkipConfirmModal && (
                <SignOutConfirmModal
                    show={showSkipConfirmModal}
                    onHide={() => setShowSkipConfirmModal(false)}
                    onConfirm={handleSkipConfirmYes}
                    message={
                        cartItems.length > 0
                            ? (
                                <>
                                    Your cart items and selections will be lost.
                                    <br />
                                    Are you sure you want to skip?
                                </>
                            )
                            : readFavouriteIsAlreadySaved()
                                ? "Are you sure you want to skip?"
                                : (
                                    <>
                                        You will lose your art created.
                                        <br />
                                        Are you sure you want to skip?
                                    </>
                                )
                    }
                    cancelLabel="No"
                    confirmLabel="Yes"
                />
            )}
            {printSizeModal && <PrintSizeModal show={printSizeModal} onHide={handleClosePrintSizeModal} />}
            {showCartModal && (
                <CartModal
                    show={showCartModal}
                    onHide={handleCloseCartModal}
                    favouriteImg={currentArt}
                    variant="pack"
                    onPayNow={() => {
                        if (!(authUser ?? getStoredAuthUser())) {
                            setShowCartModal(false);
                            pendingCheckoutAfterLoginRef.current = true;
                            setShowSigninSkipButton(false);
                            setSigninModal(true);
                            return;
                        }
                        void handlePackCheckout();
                    }}
                    onPayOnReception={() => {
                        if (!(authUser ?? getStoredAuthUser())) {
                            setShowCartModal(false);
                            pendingCheckoutAfterLoginRef.current = true;
                            setShowSigninSkipButton(false);
                            setSigninModal(true);
                            return;
                        }
                        handlePackPayOnReception();
                    }}
                />
            )}
            {showGuestEmailModal && (
                <GuestEmailModal
                    show={showGuestEmailModal}
                    onHide={() => setShowGuestEmailModal(false)}
                    onSubmit={handleGuestEmailSubmit}
                    isSubmitting={isProcessingPayment}
                    pricing={cartPricing}
                    initialEmail={
                        (authUser ?? getStoredAuthUser())?.email ||
                        (typeof scanResult?.email === "string" ? scanResult.email : "") ||
                        ""
                    }
                />
            )}
            {merchandiseModal.show && (
                <MerchantiseItemModal
                    show={merchandiseModal.show}
                    data={merchandiseModal.item}
                    onHide={handleCloseMerchandiseModal}
                    initialQuantity={merchandiseModal.initialQuantity}
                    onAddToCart={handleMerchandiseAddedToCart}
                    favouriteImg={currentArt}
                    singleStickerMockupUrl={singleStickerMockupUrl}
                />
            )}
            {packModal.show && (
                <PackItemModal
                    show={packModal.show}
                    pack={packModal.pack}
                    packIndex={packModal.index}
                    initialQuantity={packModal.initialQuantity}
                    favouriteImg={currentArt}
                    onHide={handleClosePackModal}
                    onConfirm={handlePackConfirm}
                />
            )}


        </>
    );
};

export default PrintFrameSelection;
